import { getCountryName } from '@mm/constants/countries';
import AdAccountModel, { CustomAudience } from '@mm/state/model/AdAccountModel';
import {
  MMTargeting,
  PossibleLocation,
  TargetingAudience,
} from '@mm/state/model/AdConfigModel';
import {
  AdSetWithRelationsModel,
  FBCity,
  FBCountry,
  FBCountryGroup,
  FBCustomAudience,
  FBDistanceUnit,
  FBGeoLocation,
  FBGeoLocationType,
  FBZip,
} from '@mm/state/model/AdSetModel';
import { capitalize, omit, pick } from 'lodash';
import { FB_AGE_MAX, FB_AGE_MIN } from '../../../constants/constants';

export const usLocation: FBCountry = {
  key: 'US',
  name: 'United States',
  countryCode: 'US',
  type: 'country',
};

export const defaultTargeting: () => MMTargeting = () => ({
  includedLocations: [usLocation],
  excludedLocations: [],
  audiences: [],
  excludedAudiences: [],
  locationTypes: ['home', 'recent'],
  excludedLocationTypes: ['home'],
  ageMin: 18,
  ageMax: 65,
});

const locationTypesMap: {
  [K in keyof Omit<
    FBGeoLocation,
    'locationTypes' | 'customLocations'
  >]?: FBGeoLocationType;
} = {
  zips: 'zip',
  electoralDistricts: 'electoral_district',
  countryGroups: 'country_group',
  countries: 'country',
  regions: 'region',
  largeGeoAreas: 'large_geo_area',
  mediumGeoAreas: 'medium_geo_area',
  smallGeoAreas: 'small_geo_area',
  cities: 'city',
  subcities: 'subcity',
  geoMarkets: 'geo_market',
  neighborhoods: 'neighborhood',
  subneighborhoods: 'subneighborhood',
  metroAreas: 'metro_area',
};

const prepareLocation = (location: FBGeoLocation) => {
  const mappedLocation: PossibleLocation[] = [];
  Object.keys(locationTypesMap).forEach(
    (key: keyof typeof locationTypesMap) => {
      const values = location[key] || [];
      // typescript union types are meh :(
      const mappedValues = (values as any[]).map(
        (entity: PossibleLocation | string): PossibleLocation =>
          processEntity(entity, key),
      );
      mappedLocation.push(...mappedValues);
    },
  );
  return mappedLocation;
};

const processEntity = (
  entity: PossibleLocation | string,
  key: keyof typeof locationTypesMap,
): PossibleLocation => {
  const baseEntity = { type: locationTypesMap[key] as FBGeoLocationType };
  if (key === 'countries' && typeof entity === 'string') {
    return {
      ...baseEntity,
      key: entity,
      name: getCountryName(entity),
      countryCode: entity,
    } as FBCountry;
  }
  if (key === 'countryGroups' && typeof entity === 'string') {
    return {
      ...baseEntity,
      key: entity,
      name: capitalize(entity),
    } as FBCountryGroup;
  }
  return {
    ...baseEntity,
    ...(entity as PossibleLocation),
    ...('regionId' in (entity as PossibleLocation)
      ? { regionId: Number((entity as FBCity).regionId) }
      : {}),
    ...('radius' in (entity as PossibleLocation)
      ? { radius: Number((entity as FBCity).radius) }
      : {}),
    ...('primaryCityId' in (entity as PossibleLocation)
      ? { primaryCityId: Number((entity as FBZip).primaryCityId) }
      : {}),
  } as PossibleLocation;
};

const prepareAudiences = (
  adAccount: AdAccountModel | null,
  audiences: FBCustomAudience[],
) => {
  return audiences.map((audience: FBCustomAudience) => {
    if (typeof audience === 'string') {
      const accountAudience = (adAccount?.customAudiences || []).find(
        aud => aud.id === audience,
      );
      return {
        id: audience,
        mm: false,
        name: accountAudience ? accountAudience.name : 'Unknown audience',
      };
    }
    return { ...audience, mm: false };
  });
};

const specialLocationTypes = [
  'country',
  'country_group',
  'city',
  'large_geo_area',
  'medium_geo_area',
  'region',
];

const specialAudienceType = 'REGULATED_CATEGORIES_AUDIENCE';
const lookalikeAudienceType = 'LOOKALIKE';

const audienceFilter = (
  audience: TargetingAudience,
  adAccount: AdAccountModel | null,
  isSpecial: boolean,
) => {
  if (audience.mm || adAccount === null) {
    return true;
  }
  const remoteAudience = (adAccount.customAudiences || []).find(
    a => a.id === audience.id,
  );
  if (!remoteAudience) {
    return true;
  }
  if (!audienceValid(remoteAudience)) {
    return false;
  }
  return (
    remoteAudience.subtype !==
    (isSpecial ? lookalikeAudienceType : specialAudienceType)
  );
};

const validOperationCodes = [0, 200, 400, 441];
const validDeliveryCodes = [200, 300];

export const filteredAudiences = (
  audiences: CustomAudience[],
  special: boolean,
) =>
  audiences.filter((aud: CustomAudience) => {
    if (!audienceValid(aud)) {
      return false;
    }
    return special
      ? aud.subtype !== lookalikeAudienceType
      : aud.subtype !== specialAudienceType;
  });

export const audienceValid = (audience: CustomAudience) => {
  const operationCode = audience?.operationStatus?.code;
  const deliveryCode = audience?.deliveryStatus?.code;
  if (
    operationCode !== undefined &&
    validOperationCodes.indexOf(operationCode) === -1
  ) {
    return false;
  }
  return !(
    deliveryCode !== undefined &&
    validDeliveryCodes.indexOf(deliveryCode) === -1
  );
};

export const prepareNormalTargeting = (
  targeting: MMTargeting,
  adAccount: AdAccountModel | null,
) => {
  const filter = (audience: TargetingAudience) =>
    audienceFilter(audience, adAccount, false);
  return {
    ...targeting,
    audiences: targeting.audiences.filter(filter),
    excludedAudiences: targeting.excludedAudiences.filter(filter),
  };
};

export const prepareSpecialTargeting = (
  targeting: MMTargeting,
  adAccount: AdAccountModel | null,
) => {
  const filter = (audience: TargetingAudience) =>
    audienceFilter(audience, adAccount, true);
  return {
    ...omit(targeting, 'genders'),
    excludedLocations: [],
    audiences: targeting.audiences.filter(filter),
    excludedAudiences: targeting.excludedAudiences.filter(filter),
    includedLocations: targeting.includedLocations.filter(
      location => specialLocationTypes.indexOf(location.type || '') !== -1,
    ),
    ageMin: FB_AGE_MIN,
    ageMax: FB_AGE_MAX,
  };
};

export const minDistance = (unit: FBDistanceUnit) =>
  unit === 'mile' ? 15 : 25;
export const mapAdSetTargeting = (
  adAccount: AdAccountModel | null,
  adSet?: AdSetWithRelationsModel | null,
): MMTargeting => {
  if (!adSet || !adSet.targeting) {
    return defaultTargeting();
  }
  const adSetTargeting = adSet.targeting;
  const targeting: MMTargeting = {
    ...defaultTargeting(),
    ageMin: adSetTargeting.ageMin,
    ageMax: adSetTargeting.ageMax,
    ...pick(
      adSetTargeting,
      'genders',
      'facebookPositions',
      'instagramPositions',
      'messengerPositions',
      'publisherPlatforms',
      'devicePlatforms',
    ),
    ...(adSetTargeting.excludedGeoLocations
      ? {
          excludedLocationTypes:
            adSetTargeting.excludedGeoLocations.locationTypes,
        }
      : {}),
    ...(adSetTargeting.geoLocations
      ? { locationTypes: adSetTargeting.geoLocations.locationTypes }
      : {}),
  };
  if (adSetTargeting.geoLocations) {
    targeting.includedLocations = prepareLocation(adSetTargeting.geoLocations);
  }
  if (adSetTargeting.excludedGeoLocations) {
    targeting.excludedLocations = prepareLocation(
      adSetTargeting.excludedGeoLocations,
    );
  }
  if (adSetTargeting.customAudiences) {
    targeting.audiences = prepareAudiences(
      adAccount,
      adSetTargeting.customAudiences,
    );
  }
  if (adSetTargeting.excludedCustomAudiences) {
    targeting.excludedAudiences = prepareAudiences(
      adAccount,
      adSetTargeting.excludedCustomAudiences,
    );
  }
  return targeting;
};
